import { generatePath as routerGeneratePath } from 'react-router';

interface Route<T extends Record<string, string | undefined>> {
  routerPath: string;
  relativePath: string;
  generatePath: (params: T) => string;
}

export type App = Record<string, never>;
export const App: Route<App> = {
  routerPath: '/*',
  relativePath: '*',
  generatePath: (params) => routerGeneratePath(App.routerPath, params),
};

export type Home = Record<string, never>;
export const Home: Route<Home> = {
  routerPath: '/',
  relativePath: '/',
  generatePath: (params) => routerGeneratePath(Home.routerPath, params),
};

export type KPI = Record<string, never>;
export const KPI: Route<KPI> = {
  routerPath: '/kpi',
  relativePath: 'kpi/*',
  generatePath: (params) => routerGeneratePath(KPI.routerPath, params),
};

export type Vehicles = Record<string, never>;
export const Vehicles: Route<Vehicles> = {
  routerPath: '/vehicles',
  relativePath: 'vehicles/*',
  generatePath: (params) => routerGeneratePath(Vehicles.routerPath, params),
};

export type Vehicle = { vehicle_id: string };
export const Vehicle: Route<Vehicle> = {
  routerPath: '/vehicles/:vehicle_id',
  relativePath: ':vehicle_id',
  generatePath: (params) => routerGeneratePath(Vehicle.routerPath, params),
};

export type Tours = Record<string, never>;
export const Tours: Route<Tours> = {
  routerPath: '/tours',
  relativePath: 'tours/*',
  generatePath: (params) => routerGeneratePath(Tours.routerPath, params),
};

export type Tour = { tour_id: string };
export const Tour: Route<Tour> = {
  routerPath: '/tours/:tour_id',
  relativePath: ':tour_id/*',
  generatePath: (params) => routerGeneratePath(Tour.routerPath, params),
};

export type TourStops = { tour_id: string };
export const TourStops: Route<TourStops> = {
  routerPath: '/tours/:tour_id/stops',
  relativePath: 'stops',
  generatePath: (params) => routerGeneratePath(TourStops.routerPath, params),
};

export type TourDeliveries = { tour_id: string };
export const TourDeliveries: Route<TourDeliveries> = {
  routerPath: '/tours/:tour_id/deliveries',
  relativePath: 'deliveries',
  generatePath: (params) => routerGeneratePath(TourDeliveries.routerPath, params),
};

export type TourVisibility = { tour_id: string };
export const TourVisibility: Route<TourVisibility> = {
  routerPath: '/tours/:tour_id/visibility',
  relativePath: 'visibility',
  generatePath: (params) => routerGeneratePath(TourVisibility.routerPath, params),
};

export type TourEvents = { tour_id: string };
export const TourEvents: Route<TourEvents> = {
  routerPath: '/tours/:tour_id/events',
  relativePath: 'events',
  generatePath: (params) => routerGeneratePath(TourEvents.routerPath, params),
};

export type TourCarbon = { tour_id: string };
export const TourCarbon: Route<TourCarbon> = {
  routerPath: '/tours/:tour_id/carbon',
  relativePath: 'carbon',
  generatePath: (params) => routerGeneratePath(TourCarbon.routerPath, params),
};

export type TourUnified = { tour_id: string };
export const TourUnified: Route<TourUnified> = {
  routerPath: '/tours/:tour_id/unified',
  relativePath: 'unified',
  generatePath: (params) => routerGeneratePath(TourUnified.routerPath, params),
};

export type TourKafka = { tour_id: string };
export const TourKafka: Route<TourKafka> = {
  routerPath: '/tours/:tour_id/kafka',
  relativePath: 'kafka',
  generatePath: (params) => routerGeneratePath(TourKafka.routerPath, params),
};

export type TourTraffic = { tour_id: string };
export const TourTraffic: Route<TourTraffic> = {
  routerPath: '/tours/:tour_id/traffic',
  relativePath: 'traffic',
  generatePath: (params) => routerGeneratePath(TourTraffic.routerPath, params),
};

export type TourAnalyze = { tour_id: string };
export const TourAnalyze: Route<TourAnalyze> = {
  routerPath: '/tours/:tour_id/analyze',
  relativePath: 'analyze',
  generatePath: (params) => routerGeneratePath(TourAnalyze.routerPath, params),
};

export type TourAnalytics = { tour_id: string };
export const TourAnalytics: Route<TourAnalytics> = {
  routerPath: '/tours/:tour_id/analytics',
  relativePath: 'analytics',
  generatePath: (params) => routerGeneratePath(TourAnalytics.routerPath, params),
};

export type TourRelatedTransports = { tour_id: string };
export const TourRelatedTransports: Route<TourRelatedTransports> = {
  routerPath: '/tours/:tour_id/related_transports',
  relativePath: 'related_transports',
  generatePath: (params) => routerGeneratePath(TourRelatedTransports.routerPath, params),
};

export type Companies = Record<string, never>;
export const Companies: Route<Companies> = {
  routerPath: '/companies',
  relativePath: 'companies/*',
  generatePath: (params) => routerGeneratePath(Companies.routerPath, params),
};

export type CompanySupportedRoutes = { company_id: string; slug_company_name?: string; section?: string };
export const CompanySupportedRoutes: Route<CompanySupportedRoutes> = {
  routerPath: '/companies/:company_id/:slug_company_name?/:section',
  relativePath: ':company_id/:slug_company_name?/:section',
  generatePath: (params) => routerGeneratePath(CompanySupportedRoutes.routerPath, params),
};

export type Company = { company_id: string; slug_company_name?: string };
export const Company: Route<Company> = {
  routerPath: '/companies/:company_id/:slug_company_name?',
  relativePath: ':company_id/:slug_company_name?/*',
  generatePath: (params) => routerGeneratePath(Company.routerPath, params),
};

export type CompanyCreate = Record<string, never>;
export const CompanyCreate: Route<CompanyCreate> = {
  routerPath: '/companies/new',
  relativePath: 'new',
  generatePath: (params) => routerGeneratePath(CompanyCreate.routerPath, params),
};

export type CompanyEdit = { company_id: string; slug_company_name?: string };
export const CompanyEdit: Route<Company> = {
  routerPath: '/companies/:company_id/:slug_company_name?/edit',
  relativePath: 'edit',
  generatePath: (params) => routerGeneratePath(CompanyEdit.routerPath, params),
};

export type CompanyUsers = { company_id: string; slug_company_name?: string };
export const CompanyUsers: Route<CompanyUsers> = {
  routerPath: '/companies/:company_id/:slug_company_name?/users',
  relativePath: 'users',
  generatePath: (params) => routerGeneratePath(CompanyUsers.routerPath, params),
};

export type CompanyUser = { company_id: string; user_id: string; slug_company_name?: string };
export const CompanyUser: Route<CompanyUser> = {
  routerPath: '/companies/:company_id/:slug_company_name?/users/:user_id',
  relativePath: 'users/:user_id',
  generatePath: (params) => routerGeneratePath(CompanyUser.routerPath, params),
};

export type CompanyUserCreate = { company_id: string; slug_company_name?: string };
export const CompanyUserCreate: Route<CompanyUserCreate> = {
  routerPath: '/companies/:company_id/:slug_company_name?/users/new',
  relativePath: 'users/new',
  generatePath: (params) => routerGeneratePath(CompanyUserCreate.routerPath, params),
};

export type CompanyUserGroups = { company_id: string; slug_company_name?: string };
export const CompanyUserGroups: Route<CompanyUserGroups> = {
  routerPath: '/companies/:company_id/:slug_company_name?/groups',
  relativePath: 'groups',
  generatePath: (params) => routerGeneratePath(CompanyUserGroups.routerPath, params),
};

export type CompanyUserGroup = { company_id: string; group_id: string; slug_company_name?: string };
export const CompanyUserGroup: Route<CompanyUserGroup> = {
  routerPath: '/companies/:company_id/:slug_company_name?/groups/:group_id',
  relativePath: 'groups/:group_id',
  generatePath: (params) => routerGeneratePath(CompanyUserGroup.routerPath, params),
};

export type CompanyTelematicsIntegrations = { company_id: string; slug_company_name?: string };
export const CompanyTelematicsIntegrations: Route<CompanyTelematicsIntegrations> = {
  routerPath: '/companies/:company_id/:slug_company_name?/integrations',
  relativePath: 'integrations',
  generatePath: (params) => routerGeneratePath(CompanyTelematicsIntegrations.routerPath, params),
};

export type CompanyTelematicsIntegration = {
  company_id: string;
  telematics_integration_id: string;
  slug_company_name?: string;
};
export const CompanyTelematicsIntegration: Route<CompanyTelematicsIntegration> = {
  routerPath: '/companies/:company_id/:slug_company_name?/integrations/:telematics_integration_id',
  relativePath: 'integrations/:telematics_integration_id',
  generatePath: (params) => routerGeneratePath(CompanyTelematicsIntegration.routerPath, params),
};

export type CompanyTelematicsIntegrationCreate = { company_id: string; slug_company_name?: string };
export const CompanyTelematicsIntegrationCreate: Route<CompanyTelematicsIntegrationCreate> = {
  routerPath: '/companies/:company_id/:slug_company_name?/integrations/new',
  relativePath: 'integrations/new',
  generatePath: (params) => routerGeneratePath(CompanyTelematicsIntegrationCreate.routerPath, params),
};

export type CompanyWebhookSubscriptionCreate = { company_id: string; slug_company_name?: string };
export const CompanyWebhookSubscriptionCreate: Route<CompanyWebhookSubscriptionCreate> = {
  routerPath: '/companies/:company_id/:slug_company_name?/integrations/webhooks/new',
  relativePath: 'integrations/webhooks/new',
  generatePath: (params) => routerGeneratePath(CompanyWebhookSubscriptionCreate.routerPath, params),
};

export type CompanyWebhookSubscription = {
  company_id: string;
  webhook_subscription_id: string;
  slug_company_name?: string;
};
export const CompanyWebhookSubscription: Route<CompanyWebhookSubscription> = {
  routerPath: '/companies/:company_id/:slug_company_name?/integrations/webhooks/:webhook_subscription_id',
  relativePath: 'integrations/webhooks/:webhook_subscription_id',
  generatePath: (params) => routerGeneratePath(CompanyWebhookSubscription.routerPath, params),
};

export type CompanyPlatforms = { company_id: string; slug_company_name?: string };
export const CompanyPlatforms: Route<CompanyPlatforms> = {
  routerPath: '/companies/:company_id/:slug_company_name?/platforms',
  relativePath: 'platforms',
  generatePath: (params) => routerGeneratePath(CompanyPlatforms.routerPath, params),
};

export type CompanyPlatform = { company_id: string; platform_id: string; slug_company_name?: string };
export const CompanyPlatform: Route<CompanyPlatform> = {
  routerPath: '/companies/:company_id/:slug_company_name?/platforms/:platform_id',
  relativePath: 'platforms/:platform_id',
  generatePath: (params) => routerGeneratePath(CompanyPlatform.routerPath, params),
};

export type CompanyPlatformCreate = { company_id: string; slug_company_name?: string };
export const CompanyPlatformCreate: Route<CompanyPlatformCreate> = {
  routerPath: '/companies/:company_id/:slug_company_name?/platforms/new',
  relativePath: 'platforms/new',
  generatePath: (params) => routerGeneratePath(CompanyPlatformCreate.routerPath, params),
};

export type CompanyRelationships = { company_id: string; slug_company_name?: string };
export const CompanyRelationships: Route<CompanyRelationships> = {
  routerPath: '/companies/:company_id/:slug_company_name?/relationships',
  relativePath: 'relationships',
  generatePath: (params) => routerGeneratePath(CompanyRelationships.routerPath, params),
};

export type CompanyRelationship = { company_id: string; company_relationship_id: string; slug_company_name?: string };
export const CompanyRelationship: Route<CompanyRelationship> = {
  routerPath: '/companies/:company_id/:slug_company_name?/relationships/:company_relationship_id',
  relativePath: 'relationships/:company_relationship_id',
  generatePath: (params) => routerGeneratePath(CompanyRelationship.routerPath, params),
};

export type CompanyRelationshipCreate = { company_id: string; slug_company_name?: string };
export const CompanyRelationshipCreate: Route<CompanyRelationshipCreate> = {
  routerPath: '/companies/:company_id/:slug_company_name?/relationships/new',
  relativePath: 'relationships/new',
  generatePath: (params) => routerGeneratePath(CompanyRelationshipCreate.routerPath, params),
};

export type CompanyGroup = { company_id: string; slug_company_name?: string };
export const CompanyGroup: Route<CompanyGroup> = {
  routerPath: '/companies/:company_id/:slug_company_name?/relationships/company-group',
  relativePath: 'relationships/company-group',
  generatePath: (params) => routerGeneratePath(CompanyGroup.routerPath, params),
};

export type CompanyAssets = { company_id: string; slug_company_name?: string };
export const CompanyAssets: Route<CompanyAssets> = {
  routerPath: '/companies/:company_id/:slug_company_name?/assets',
  relativePath: 'assets',
  generatePath: (params) => routerGeneratePath(CompanyAssets.routerPath, params),
};

export type CompanyOnboarding = { company_id: string; slug_company_name?: string };
export const CompanyOnboarding: Route<CompanyOnboarding> = {
  routerPath: '/companies/:company_id/:slug_company_name?/onboarding',
  relativePath: 'onboarding',
  generatePath: (params) => routerGeneratePath(CompanyOnboarding.routerPath, params),
};

export type CompanyVehicleGrants = { company_id: string; slug_company_name?: string };
export const CompanyVehicleGrants: Route<CompanyVehicleGrants> = {
  routerPath: '/companies/:company_id/:slug_company_name?/grants',
  relativePath: 'grants',
  generatePath: (params) => routerGeneratePath(CompanyVehicleGrants.routerPath, params),
};

export type CompanyCustomerPartners = { company_id: string; slug_company_name?: string };
export const CompanyCustomerPartners: Route<CompanyCustomerPartners> = {
  routerPath: '/companies/:company_id/:slug_company_name?/shippers',
  relativePath: 'shippers',
  generatePath: (params) => routerGeneratePath(CompanyCustomerPartners.routerPath, params),
};

export type CompanyCarrierPartners = { company_id: string; slug_company_name?: string };
export const CompanyCarrierPartners: Route<CompanyCarrierPartners> = {
  routerPath: '/companies/:company_id/:slug_company_name?/carriers',
  relativePath: 'carriers',
  generatePath: (params) => routerGeneratePath(CompanyCarrierPartners.routerPath, params),
};

export type CompanyConfiguration = { company_id: string; slug_company_name?: string };
export const CompanyConfiguration: Route<CompanyConfiguration> = {
  routerPath: '/companies/:company_id/:slug_company_name?/configuration',
  relativePath: 'configuration',
  generatePath: (params) => routerGeneratePath(CompanyConfiguration.routerPath, params),
};

export type CompanyFeatureFlags = { company_id: string; slug_company_name?: string };
export const CompanyFeatureFlags: Route<CompanyFeatureFlags> = {
  routerPath: '/companies/:company_id/:slug_company_name?/features',
  relativePath: 'features',
  generatePath: (params) => routerGeneratePath(CompanyFeatureFlags.routerPath, params),
};

export type CompanyDashboards = { company_id: string; slug_company_name?: string };
export const CompanyDashboards: Route<CompanyDashboards> = {
  routerPath: '/companies/:company_id/:slug_company_name?/dashboards',
  relativePath: 'dashboards',
  generatePath: (params) => routerGeneratePath(CompanyDashboards.routerPath, params),
};

export type CompanyKafka = { company_id: string; slug_company_name?: string };
export const CompanyKafka: Route<CompanyKafka> = {
  routerPath: '/companies/:company_id/:slug_company_name?/kafka',
  relativePath: 'kafka',
  generatePath: (params) => routerGeneratePath(CompanyKafka.routerPath, params),
};

export type CompanyFileStorage = { company_id: string; slug_company_name?: string };
export const CompanyFileStorage: Route<CompanyFileStorage> = {
  routerPath: '/companies/:company_id/:slug_company_name?/file-storage',
  relativePath: 'file-storage',
  generatePath: (params) => routerGeneratePath(CompanyFileStorage.routerPath, params),
};

export type CompanyLem = { company_id: string; slug_company_name?: string };
export const CompanyLem: Route<CompanyLem> = {
  routerPath: '/companies/:company_id/:slug_company_name?/lem',
  relativePath: 'lem',
  generatePath: (params) => routerGeneratePath(CompanyLem.routerPath, params),
};

export type CompanyAgreement = { company_id: string; slug_company_name?: string };
export const CompanyAgreement: Route<CompanyAgreement> = {
  routerPath: '/companies/:company_id/:slug_company_name?/agreement',
  relativePath: 'agreement',
  generatePath: (params) => routerGeneratePath(CompanyAgreement.routerPath, params),
};

export type Users = Record<string, string>;
export const Users: Route<Users> = {
  routerPath: '/users/',
  relativePath: 'users/*',
  generatePath: (params) => routerGeneratePath(Users.routerPath, params),
};

export type User = { user_id: string };
export const User: Route<User> = {
  routerPath: '/users/:user_id',
  relativePath: ':user_id',
  generatePath: (params) => routerGeneratePath(User.routerPath, params),
};

export type Telematics = Record<string, string>;
export const Telematics: Route<Telematics> = {
  routerPath: '/telematics',
  relativePath: 'telematics/*',
  generatePath: (params) => routerGeneratePath(Telematics.routerPath, params),
};

export type TelematicsProviders = Record<string, string>;
export const TelematicsProviders: Route<TelematicsProviders> = {
  routerPath: '/telematics/providers',
  relativePath: 'providers',
  generatePath: (params) => routerGeneratePath(TelematicsProviders.routerPath, params),
};

export type TelematicsProvider = { provider_id: string };
export const TelematicsProvider: Route<TelematicsProvider> = {
  routerPath: '/telematics/providers/:provider_id',
  relativePath: 'providers/:provider_id',
  generatePath: (params) => routerGeneratePath(TelematicsProvider.routerPath, params),
};

export type TelematicIntegrations = Record<string, never>;
export const TelematicIntegrations: Route<TelematicIntegrations> = {
  routerPath: '/telematics/integrations',
  relativePath: 'integrations',
  generatePath: (params) => routerGeneratePath(TelematicIntegrations.routerPath, params),
};

export type Miscellaneous = Record<string, never>;
export const Miscellaneous: Route<Miscellaneous> = {
  routerPath: '/miscellaneous',
  relativePath: 'miscellaneous/*',
  generatePath: (params) => routerGeneratePath(Miscellaneous.routerPath, params),
};

export type TransportAnalyzer = Record<string, never>;
export const TransportAnalyzer: Route<TransportAnalyzer> = {
  routerPath: '/miscellaneous/transport_analyzer',
  relativePath: 'transport_analyzer',
  generatePath: (params) => routerGeneratePath(TransportAnalyzer.routerPath, params),
};

export type Intercom = Record<string, never>;
export const Intercom: Route<Intercom> = {
  routerPath: '/miscellaneous/intercom',
  relativePath: 'intercom',
  generatePath: (params) => routerGeneratePath(Intercom.routerPath, params),
};

export type NotificationPreviews = Record<string, never>;
export const NotificationPreviews: Route<NotificationPreviews> = {
  routerPath: '/miscellaneous/notification_previews',
  relativePath: 'notification_previews',
  generatePath: (params) => routerGeneratePath(NotificationPreviews.routerPath, params),
};

export type OvdTokens = Record<string, never>;
export const OvdTokens: Route<OvdTokens> = {
  routerPath: '/miscellaneous/ovd_tokens',
  relativePath: 'ovd_tokens/*',
  generatePath: (params) => routerGeneratePath(OvdTokens.routerPath, params),
};

export type OvdTokenDetail = { token_id: string };
export const OvdTokenDetail: Route<OvdTokenDetail> = {
  routerPath: '/miscellaneous/ovd_tokens/:token_id',
  relativePath: ':token_id',
  generatePath: (params) => routerGeneratePath(OvdTokenDetail.routerPath, params),
};

export type OvdTokenCreate = Record<string, never>;
export const OvdTokenCreate: Route<OvdTokenCreate> = {
  routerPath: '/miscellaneous/ovd_tokens/new',
  relativePath: 'new',
  generatePath: (params) => routerGeneratePath(OvdTokenCreate.routerPath, params),
};

export type ApiUsage = Record<string, never>;
export const ApiUsage: Route<ApiUsage> = {
  routerPath: '/visibility_api',
  relativePath: 'visibility_api/*',
  generatePath: (params) => routerGeneratePath(ApiUsage.routerPath, params),
};

export type Login = Record<string, never>;
export const Login: Route<Login> = {
  routerPath: '/login',
  relativePath: 'login',
  generatePath: (params) => routerGeneratePath(Login.routerPath, params),
};

export type Logout = Record<string, never>;
export const Logout: Route<Logout> = {
  routerPath: '/logout',
  relativePath: 'logout',
  generatePath: (params) => routerGeneratePath(Logout.routerPath, params),
};

export type MobileCommandCenter = Record<string, never>;
export const MobileCommandCenter: Route<MobileCommandCenter> = {
  routerPath: '/mobile',
  relativePath: 'mobile/*',
  generatePath: (params) => routerGeneratePath(MobileCommandCenter.routerPath, params),
};

export type MobileTours = Record<string, never>;
export const MobileTours: Route<MobileTours> = {
  routerPath: '/mobile/tours',
  relativePath: 'tours',
  generatePath: (params) => routerGeneratePath(MobileTours.routerPath, params),
};

export type MobileTour = { tour_id: string };
export const MobileTour: Route<MobileTour> = {
  routerPath: '/mobile/tours/:tour_id',
  relativePath: 'tours/:tour_id/*',
  generatePath: (params) => routerGeneratePath(MobileTour.routerPath, params),
};

export type MobileCompanies = Record<string, never>;
export const MobileCompanies: Route<MobileCompanies> = {
  routerPath: '/mobile/companies',
  relativePath: 'companies',
  generatePath: (params) => routerGeneratePath(MobileCompanies.routerPath, params),
};

export type MobileCompany = { company_id: string };
export const MobileCompany: Route<MobileCompany> = {
  routerPath: '/mobile/companies/:company_id',
  relativePath: 'companies/:company_id/*',
  generatePath: (params) => routerGeneratePath(MobileCompany.routerPath, params),
};

export type MobileInsights = Record<string, never>;
export const MobileInsights: Route<MobileInsights> = {
  routerPath: '/mobile/insights',
  relativePath: 'insights',
  generatePath: (params) => routerGeneratePath(MobileInsights.routerPath, params),
};
